import { useMemo, useState } from "react";
import Seo from "@/components/layout/Seo";
import JsonLd from "@/components/layout/JsonLd";
import Breadcrumbs from "@/components/layout/Breadcrumbs";
import SectionCTA from "@/components/marketing/SectionCTA";
import InlineCTA from "@/components/marketing/InlineCTA";
import PrestationsCrossLinks from "@/components/marketing/PrestationsCrossLinks";
import Reveal from "@/components/layout/Reveal";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import { PROJECTS, REALISATION_CATEGORIES } from "@/lib/site";
import { buildBreadcrumb } from "@/lib/schema";
import { ArrowUpRight, MapPin } from "lucide-react";

export default function RealisationsPage() {
    const [filter, setFilter] = useState("Tous");
    const [active, setActive] = useState(null);

    const filtered = useMemo(() => {
        if (filter === "Tous") return PROJECTS;
        return PROJECTS.filter((p) => p.category === filter);
    }, [filter]);

    return (
        <>
            <Seo
                title="Réalisations"
                description="Galerie des réalisations de RCPS Terrassement : terrassement, démolition, génie civil, voirie, enrobés, aménagements extérieurs et enrochage à Genève et en Suisse romande."
            />
            <JsonLd
                data={buildBreadcrumb([
                    { name: "Accueil", path: "/" },
                    { name: "Réalisations", path: "/realisations" },
                ])}
                id="breadcrumb-realisations"
            />
            {/* Hero */}
            <section className="relative bg-navy-700 text-white overflow-hidden">
                <img
                    src="/rcps-assets/realisation-batiment-collectif-vd.jpg"
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-45"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-navy-800/80 via-navy-700/70 to-navy-700" />
                <div className="absolute inset-0 bg-topo-dark opacity-40 mix-blend-overlay" />
                <div className="absolute inset-0 noise-overlay" />
                <div className="relative rcps-container pt-24 md:pt-28 pb-16 md:pb-24 max-w-4xl">
                    <Reveal>
                        <Breadcrumbs
                            variant="onImage"
                            trail={[
                                { name: "Accueil", path: "/" },
                                { name: "Réalisations", path: "/realisations" },
                            ]}
                        />
                    </Reveal>
                    <Reveal delay={0.05}>
                        <p className="kicker text-white/80 mt-5"><span className="hairline bg-white/60" /> Réalisations</p>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h1 className="mt-4 font-heading text-4xl md:text-6xl font-semibold leading-[1.05] tracking-tight">
                            Exemples de réalisations et typologies de chantiers.
                        </h1>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="mt-6 text-white/80 max-w-2xl text-base md:text-lg leading-relaxed">
                            Découvrez les principaux types de projets que RCPS Terrassement peut étudier et réaliser à Genève et en Suisse romande : terrassement, démolition, génie civil, voirie, enrobés, aménagements extérieurs et enrochement.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* Filtres + Grille */}
            <section className="bg-white">
                <div className="rcps-container py-16 md:py-20">
                    <div data-testid="realisations-filters" className="flex flex-wrap gap-2 border-b border-concrete-200 pb-6">
                        {REALISATION_CATEGORIES.map((c) => (
                            <button
                                key={c}
                                data-testid="realisations-filter-tab"
                                onClick={() => setFilter(c)}
                                className={`px-4 h-10 rounded-md text-sm font-medium transition ${
                                    filter === c
                                        ? "bg-navy-700 text-white"
                                        : "bg-concrete-50 text-navy-700 hover:bg-concrete-100"
                                }`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                    <div data-testid="realisations-grid" className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {filtered.map((p, i) => (
                            <Reveal key={p.id} delay={(i % 3) * 0.05}>
                                <button
                                    data-testid="realisations-item"
                                    onClick={() => setActive(p)}
                                    className="group text-left block w-full relative overflow-hidden rounded-md border border-concrete-200 hover:border-navy-700 transition"
                                >
                                    <div className="aspect-[4/5] overflow-hidden">
                                        <img
                                            src={p.image}
                                            alt={p.title}
                                            loading="lazy"
                                            className="w-full h-full object-cover transition duration-700 group-hover:scale-105"
                                        />
                                    </div>
                                    <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/85 via-black/25 to-transparent text-white">
                                        <p className="text-[11px] uppercase tracking-[0.16em] text-white/70">
                                            {p.category} · {p.location}
                                        </p>
                                        <h3 className="mt-1 font-heading text-lg font-semibold leading-snug">{p.title}</h3>
                                    </div>
                                </button>
                            </Reveal>
                        ))}
                    </div>
                    {filtered.length === 0 && (
                        <p className="mt-10 text-center text-navy-700/70">Aucune réalisation dans cette catégorie.</p>
                    )}
                </div>
            </section>

            <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
                <DialogContent data-testid="realisations-lightbox" className="max-w-4xl p-0 overflow-hidden">
                    {active && (
                        <div className="grid md:grid-cols-2">
                            <div className="aspect-[4/5] md:aspect-auto">
                                <img src={active.image} alt={active.title} className="w-full h-full object-cover" />
                            </div>
                            <div className="p-8">
                                <p className="kicker text-navy-700">
                                    <span className="hairline" /> {active.category}
                                </p>
                                <h3 className="mt-3 font-heading text-2xl md:text-3xl font-semibold text-navy-700 tracking-tight">
                                    {active.title}
                                </h3>
                                <p className="mt-2 text-sm text-navy-700/70 flex items-center gap-2">
                                    <MapPin className="w-4 h-4" /> {active.location}
                                </p>
                                <p className="mt-6 text-navy-700/80 leading-relaxed">{active.description}</p>
                                <div className="mt-8 flex flex-wrap gap-3">
                                    <Link
                                        to="/contact"
                                        className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-navy-700 text-white font-semibold hover:bg-navy-800 transition"
                                    >
                                        Demander un projet similaire <ArrowUpRight className="w-4 h-4" />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <InlineCTA
                variant="navy"
                title="Un projet similaire ? Parlez-nous de votre chantier."
                text="Nous étudions votre demande de terrassement, démolition, génie civil, voirie, enrobés, aménagement extérieur ou enrochement — puis revenons vers vous avec une proposition adaptée."
                primary={{ label: "Demander un devis", to: "/contact" }}
                secondary={{ label: "À propos de nous", to: "/entreprise" }}
                dataTestId="realisations-inline-cta"
            />

            <PrestationsCrossLinks
                kicker="Nos prestations"
                title="Explorez chaque prestation en détail."
                showRealisations={false}
            />

            <SectionCTA />
        </>
    );
}
